// Server validation codes → the words an organiser reads
// (bead hf-e3b-circle-people-r3z.2).
//
// Same job as features/claim/claimCopy.ts and features/calendarFeed/feedCopy.ts:
// contracts stays copy-free and product-language stays schema-free, so the mapping
// lives here where both are already dependencies.
//
// `blockIssues` in convex/domain/circleSettings.ts returns codes like
// `afternoon_ends_before_it_starts`. They are deliberately machine-shaped — they cross
// the wire, and a sentence crossing the wire is a sentence that cannot be changed
// without a deploy. Turning them into English is this file's whole job.

import type { CommonRejection } from "@herefor/contracts";
import type { TimeBlockId } from "@herefor/domain-kernel";
import {
  CIRCLE_SETTINGS_MESSAGES,
  DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES,
  ERROR_MESSAGES,
  SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES,
  SUPPORTED_PERSON_NAME_MESSAGES,
  type ActionableMessage,
} from "@herefor/product-language";

/** The block names, in day order, as a person says them. */
export const BLOCK_NAMES: Readonly<Record<TimeBlockId, string>> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
};

/**
 * Every field-level validation code the settings mutations can return, as a sentence.
 *
 * Deduplicated: three malformed fields are one "use 24-hour times" line, not three
 * identical ones. Somebody who mistyped every box does not need to be told the same
 * thing three times in a stack.
 */
export function blockProblemMessages(issues: readonly string[]): readonly string[] {
  const messages: string[] = [];
  const add = (message: string) => {
    if (!messages.includes(message)) messages.push(message);
  };

  for (const issue of issues) {
    if (issue === "direct_request_expiry_days_invalid") {
      add(DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES.validation);
      continue;
    }
    if (issue === "supported_person_name_length") {
      add(SUPPORTED_PERSON_NAME_MESSAGES.validation);
      continue;
    }
    if (issue === "print_contact_person_name_length") {
      add(SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES.validation);
      continue;
    }
    if (issue.endsWith("_malformed")) {
      add(CIRCLE_SETTINGS_MESSAGES.blockProblems.malformed);
      continue;
    }
    if (issue.endsWith("_ends_before_it_starts")) {
      const blockId = issue.slice(0, -"_ends_before_it_starts".length) as TimeBlockId;
      add(CIRCLE_SETTINGS_MESSAGES.blockProblems.endsBeforeStart(BLOCK_NAMES[blockId]));
      continue;
    }
    if (issue === "morning_overlaps_afternoon") {
      add(
        CIRCLE_SETTINGS_MESSAGES.blockProblems.overlaps(BLOCK_NAMES.morning, BLOCK_NAMES.afternoon),
      );
      continue;
    }
    if (issue === "afternoon_overlaps_evening") {
      add(
        CIRCLE_SETTINGS_MESSAGES.blockProblems.overlaps(BLOCK_NAMES.afternoon, BLOCK_NAMES.evening),
      );
      continue;
    }
    // An unrecognised code is a backend that moved without this file following. Say
    // something true and general rather than rendering a raw code at somebody.
    add(ERROR_MESSAGES.serviceUnavailable.body);
  }

  return messages;
}

/**
 * The message for a rejection that is not field-level validation.
 *
 * Total over `CommonRejection` on purpose — no `default` arm — so a new member of the
 * union is a compile error here rather than a blank panel on a settings screen.
 */
export function messageFor(rejection: CommonRejection): ActionableMessage {
  switch (rejection.kind) {
    case "missing_capability":
      return ERROR_MESSAGES.permissionDenied;
    case "not_a_member":
      return ERROR_MESSAGES.accessRemoved;
    case "circle_not_found":
    case "not_found":
      return ERROR_MESSAGES.itemUnavailable;
    case "validation_failed":
      // Field-level problems are rendered beside their fields by
      // `blockProblemMessages`; this is the fallback for a validation rejection that
      // carried no issues we can place.
      return ERROR_MESSAGES.serviceUnavailable;
    // No settings call binds a revision, so this cannot arrive today. Answered rather
    // than left to a default that would swallow a genuine bug.
    case "revision_stale":
      return ERROR_MESSAGES.serviceUnavailable;
  }
}
